import * as Yup from 'yup';
import { isValid, isBefore, isAfter, parse, addYears } from 'date-fns';
import { cpf } from 'cpf-cnpj-validator';

function validateDocument(document) {
  if (document) {
    const valid = cpf.isValid(document);

    return valid;
  }
  return true;
}

function whiteSpace(value) {
  if (value) {
    const onlySpace = value.replace(/\s/g, '');
    return !!onlySpace;
  }

  return true;
}

function validateDate(value) {
  if (value) {
    const date = parse(value, 'yyyy-MM-dd', new Date());

    if (!isValid(date)) {
      return false;
    }

    return isBefore(date, new Date());
  }

  return true;
}

function testPhone(value) {
  if (value) {
    const onlyNumber = value.replace(/\D/g, '');

    return onlyNumber.length === 11 || onlyNumber.length === 10;
  }

  return true;
}

function validateFutureDate(value) {
  if (value) {
    const date = parse(value, 'yyyy-MM-dd', new Date());

    if (!isValid(date)) {
      return false;
    }

    return isAfter(date, new Date());
  }

  return true;
}


export const driverAddressSchema = Yup.object().shape({
  address: Yup.string()
    .required('Campo obrigatório')
    .typeError('Campo obrigatório')
    .test('white-space-field', 'Campo inválido', val => whiteSpace(val)),
  neighborhood: Yup.string()
    .required('Campo obrigatório')
    .typeError('Campo obrigatório')
    .test('white-space-field', 'Campo inválido', val => whiteSpace(val)),
  // A API resolve a cidade pelo id: endereço com cidade fora da base precisa escolher uma da lista
  city: Yup.object()
    .shape({
      id: Yup.number()
        .required('Selecione uma cidade da lista')
        .typeError('Selecione uma cidade da lista'),
      name: Yup.string().required('Campo obrigatório'),
    })
    .required('Campo obrigatório')
    .typeError('Campo obrigatório'),
  province: Yup.object()
    .shape({
      name: Yup.string()
        .required('Selecione uma cidade da lista')
        .typeError('Selecione uma cidade da lista'),
    })
    .required('Campo obrigatório')
    .typeError('Campo obrigatório'),
  numberless: Yup.boolean(),
  number: Yup.string()
    .nullable()
    .when('numberless', {
      is: true,
      then: schema => schema.notRequired(),
      otherwise: schema => schema.required('Campo obrigatório').typeError('Campo obrigatório'),
    }),
  cep: Yup.string()
    .required('Campo obrigatório')
    .typeError('Campo obrigatório'),
  complement: Yup.string()
    .nullable()
    .test('white-space-field', 'Campo inválido', val => whiteSpace(val)),
  addressType: Yup.object()
    .required('Campo obrigatório')
    .typeError('Campo obrigatório'),
  homeType: Yup.object()
    .nullable(),
  country: Yup.object()
    .required('Campo obrigatório')
    .typeError('Campo obrigatório'),
});

export const registerSchema = Yup.object().shape({
  generalData: Yup.object().shape({
    name: Yup.string()
      .required('Campo obrigatório')
      .test('white-space-field', 'Campo inválido', value => whiteSpace(value)),
    cpf: Yup.string()
      .required('Campo obrigatório')
      .test('invalid-document', 'CPF inválido', value => validateDocument(value)),
    dateOfBirth: Yup.string()
      .required('Campo obrigatório')
      .typeError('Data inválida')
      .test('invalid-date', 'Data inválida', value => validateDate(value)),
    cityOfBirth: Yup.object()
      .required('Campo obrigatório')
      .typeError('Campo obrigatório'),
    rgNumber: Yup.string()
      .required('Campo obrigatório')
      .typeError('Campo obrigatório')
      .test('white-space-field', 'Campo inválido', value => whiteSpace(value)),
    rgOrganEmission: Yup.string()
      .required('Campo obrigatório')
      .typeError('Campo obrigatório')
      .test('white-space-field', 'Campo inválido', value => whiteSpace(value)),
    rgOrganEmissionUf: Yup.object()
      .required('Campo obrigatório')
      .typeError('Campo obrigatório'),
    rgDispatchDate: Yup.string()
      .nullable()
      .typeError('Data inválida')
      .test('invalid-date', 'Data inválida', value => validateDate(value)),
    motherName: Yup.string()
      .required('Campo obrigatório')
      .test('white-space-field', 'Campo inválido', value => whiteSpace(value)),
    fatherName: Yup.string()
      .required('Campo obrigatório')
      .test('white-space-field', 'Campo inválido', value => whiteSpace(value)),
    civilStatus: Yup.object()
      .required('Campo obrigatório')
      .typeError('Campo obrigatório'),
    gender: Yup.object()
      .required('Campo obrigatório')
      .typeError('Campo obrigatório'),
    pis: Yup.string()
      .when('isAutonomous', {
        is: true,
        then: schema => schema
          .required('Campo obrigatório')
          .test('white-space-field', 'Campo inválido', value => whiteSpace(value)),
        otherwise: schema => schema
          .nullable()
          .test('white-space-field', 'Campo inválido', value => whiteSpace(value)),
      }),
    isAutonomous: Yup.boolean(),
  }),
  cnhData: Yup.object().shape({
    cnhNumber: Yup.string()
      .required('Campo obrigatório')
      .test('white-space-field', 'Campo inválido', value => whiteSpace(value)),
    cnhFirstCnhDate: Yup.string()
      .required('Campo obrigatório')
      .typeError('Data inválida')
      .test('invalid-date', 'Data inválida', value => validateDate(value)),
    cnhCategory: Yup.object()
      .required('Campo obrigatório')
      .typeError('Campo obrigatório'),
    cnhDateEmission: Yup.string()
      .required('Campo obrigatório')
      .typeError('Data inválida')
      .test('invalid-date', 'Data inválida', value => validateDate(value)),
    cnhDateOfExpiration: Yup.string()
      .required('Campo obrigatório')
      .typeError('Data inválida')
      .test('future-date', 'CNH vencida', value => validateFutureDate(value)),
    cnhDispatchCity: Yup.object()
      .required('Campo obrigatório')
      .typeError('Campo obrigatório'),
    cnhKey: Yup.string()
      .nullable()
      .test('white-space-field', 'Campo inválido', value => whiteSpace(value)),
  }),
  contact: Yup.object().shape({
    phone: Yup.string()
      .required('Campo obrigatório')
      .test('valid-phone', 'Telefone inválido', value => testPhone(value)),
    email: Yup.string()
      .required('Campo obrigatório')
      .email('E-mail inválido'),
  }),
  driverAddress: driverAddressSchema,
  emergencyContact: Yup.lazy(value => {
    const isRequired = value?.name || value?.phone;

    if (isRequired) {
      return Yup.object().shape({
        name: Yup.string()
          .required('Campo obrigatório')
          .test('white-space-field', 'Campo inválido', value => whiteSpace(value)),
        phone: Yup.string()
          .required('Campo obrigatório')
          .test('valid-phone', 'Telefone inválido', value => testPhone(value)),
      });
    }

    return Yup.object().shape({
      name: Yup.string()
        .nullable()
        .test('white-space-field', 'Campo inválido', value => whiteSpace(value)),
      phone: Yup.string()
        .nullable()
        .test('valid-phone', 'Telefone inválido', value => testPhone(value)),
    });
  }),
  paymentMethod: Yup.object()
    .required('Campo obrigatório')
    .typeError('Campo obrigatório'),
});

export async function validate(data, setErrors, context) {
  const errorList = {};
  try {
    await registerSchema.validate(data, {
      abortEarly: false,
      context,
    });
    return true;
  } catch (err) {
    err.inner.forEach(error => {
      errorList[error.path] = error?.message;
    });
    return false;
  } finally {
    setErrors(errorList);
  }

}
